/**
 * VCloud Information Columns
 */
import type {
    DataTableColumn,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createOptionsFormatter,
    createDateFormatter,
    createTimeRangeFormatter,
} from '@/components/ui/data-table-customized'
import type { TFunction } from 'i18next'
import { formatNumberUsage } from '@/utils/function'
import { ServiceStatus } from '@/modules/Service/types'
import ServiceContract from '@/modules/Service/components/Detail/Templates/shared/ServiceContract'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { VCloudRow, VCloudColumnVisibility } from './types'
import { parseVMConfig } from './helpers'

// ============================================================================
// Service Status Options
// ============================================================================

const SERVICE_STATUS_OPTIONS = (t: TFunction): OptionItem[] => [
    {
        label: t('service-status.waiting-confirm'),
        value: 'waiting-confirm',
        color: 'info',
    },
    {
        label: t('service-status.waiting-create-service'),
        value: 'waiting-create-service',
        color: 'warning',
    },
    {
        label: t('service-status.trialing'),
        value: 'trialing',
        color: 'primary',
    },
    { label: t('service-status.active'), value: 'active', color: 'success' },
    {
        label: t('service-status.on-resubmit-information'),
        value: 'on-resubmit-information',
        color: 'warning',
    },
    {
        label: t('service-status.pending-terminated'),
        value: 'pending-terminated',
        color: 'error',
    },
    {
        label: t('service-status.cancelled'),
        value: 'cancelled',
        color: 'secondary',
    },
    { label: t('service-status.rejected'), value: 'rejected', color: 'error' },
]

// ============================================================================
// Column Configuration
// ============================================================================

interface CreateColumnsOptions {
    t: TFunction
    expandedServices?: Set<string>
    onToggleExpand?: (serviceId: string) => void
    columnVisibility?: VCloudColumnVisibility
}

export const createColumns = ({
    t,
    expandedServices,
    onToggleExpand,
    columnVisibility,
}: CreateColumnsOptions): DataTableColumn<VCloudRow>[] => {
    const columns: DataTableColumn<VCloudRow>[] = [
        {
            key: 'service_name',
            title: t('service_name'),
            width: 320,
            pinned: 'left',
            render: (_, row) => {
                if (row._rowType === 'service-header') {
                    const isExpanded =
                        expandedServices?.has(row.service_id || '') ?? false
                    const isMain = row._isMainService

                    if (onToggleExpand) {
                        return (
                            <div
                                className="flex items-center gap-1 cursor-pointer select-none"
                                onClick={() =>
                                    onToggleExpand(row.service_id || '')
                                }
                            >
                                {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                                {!isMain && (
                                    <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded mr-1 uppercase">
                                        Addon
                                    </span>
                                )}
                                <span>{row.name}</span>
                                {!isMain && (
                                    <span className="text-xs text-muted-foreground ml-1">
                                        ({row.service_id})
                                    </span>
                                )}
                            </div>
                        )
                    }
                    return row.name
                }
                if (row._rowType === 'type-header') {
                    return (
                        <span className="pl-6 uppercase text-muted-foreground text-xs">
                            {row.name}
                        </span>
                    )
                }
                if (row._rowType === 'summary') {
                    return row.name
                }
                const qty =
                    row.commitment_value && row.commitment_value > 1
                        ? ` (x${row.commitment_value})`
                        : ''
                return (
                    <span className="pl-10">
                        {t(`${row.name}`)}
                        {qty}
                    </span>
                )
            },
        },
        {
            key: 'vcores',
            title: 'vCores',
            width: 80,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType === 'other_service') return null
                    return row.totalVCores && row.totalVCores > 0
                        ? formatNumberUsage(row.totalVCores)
                        : null
                }
                if (row._serviceType === 'other_service') return null
                const config = parseVMConfig(row.name || '')
                return config.cores > 0 ? formatNumberUsage(config.cores) : null
            },
        },
        {
            key: 'ram',
            title: 'RAM',
            width: 80,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType === 'other_service') return null
                    return row.totalRAM && row.totalRAM > 0
                        ? formatNumberUsage(row.totalRAM)
                        : null
                }
                if (row._serviceType === 'other_service') return null
                const config = parseVMConfig(row.name || '')
                return config.ram > 0 ? formatNumberUsage(config.ram) : null
            },
        },
        {
            key: 'storage',
            title: 'Storage',
            width: 100,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalStorage && row.totalStorage > 0
                        ? formatNumberUsage(row.totalStorage)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.storage && row.storage > 0
                    ? formatNumberUsage(row.storage)
                    : null
            },
        },
    ]

    // Public IP column - only if has data
    if (columnVisibility?.hasPublicIP) {
        columns.push({
            key: 'publicIP',
            title: 'Public IP',
            width: 90,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalPublicIP && row.totalPublicIP > 0
                        ? formatNumberUsage(row.totalPublicIP)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.publicIP && row.publicIP > 0
                    ? formatNumberUsage(row.publicIP)
                    : null
            },
        })
    }

    // Floating IP column - only if has data
    if (columnVisibility?.hasFloatingIP) {
        columns.push({
            key: 'floatingIP',
            title: 'Floating IP',
            width: 100,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalFloatingIP && row.totalFloatingIP > 0
                        ? formatNumberUsage(row.totalFloatingIP)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.floatingIP && row.floatingIP > 0
                    ? formatNumberUsage(row.floatingIP)
                    : null
            },
        })
    }

    // Bandwidth column - only if has data
    if (columnVisibility?.hasBandwidth) {
        columns.push({
            key: 'bandwidth',
            title: 'Bandwidth',
            width: 100,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType === 'summary') {
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._rowType === 'service-header') {
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._rowType === 'type-header') {
                    if (row._serviceType !== 'other_service') return null
                    return row.totalBandwidth && row.totalBandwidth > 0
                        ? formatNumberUsage(row.totalBandwidth)
                        : null
                }
                if (row._serviceType !== 'other_service') return null
                return row.bandwidth && row.bandwidth > 0
                    ? formatNumberUsage(row.bandwidth)
                    : null
            },
        })
    }

    // Price columns
    columns.push(
        {
            key: 'commitment_price_per_unit',
            title: t('unit_price', { defaultValue: 'Đơn giá' }),
            width: 130,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (row.service_pricing_model === 'package') return null
                if (row._rowType !== 'item') return null
                return value
                    ? new Intl.NumberFormat('en-US').format(value)
                    : null
            },
        },
        {
            key: 'commitment_price_total',
            title: t('total_price', { defaultValue: 'Thành tiền' }),
            width: 130,
            cellClassName: 'text-right',
            render: (_, row) => {
                const isPackageRow = row.service_pricing_model === 'package'

                if (isPackageRow) {
                    if (
                        row._rowType !== 'summary' &&
                        row._rowType !== 'service-header'
                    )
                        return null
                    return new Intl.NumberFormat('en-US').format(
                        row.service_price_register ?? 0
                    )
                } else {
                    if (row._rowType === 'type-header') return null
                    if (
                        (row._rowType === 'service-header' ||
                            row._rowType === 'summary') &&
                        row.commitment_price_total
                    ) {
                        return new Intl.NumberFormat('en-US').format(
                            row.commitment_price_total
                        )
                    }
                    return row.commitment_price_total
                        ? new Intl.NumberFormat('en-US').format(
                              row.commitment_price_total
                          )
                        : null
                }
            },
        }
    )

    // Rest of columns
    columns.push(
        {
            key: 'employee_names',
            title: t('employee_sale_tracking_ids'),
            width: 180,
            render: (_, row) => {
                if (row._rowType !== 'service-header') return null
                return row?.employee_names?.join(', ') || null
            },
        },
        {
            key: 'service_start_date',
            title: t('service_start_date'),
            width: 110,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType !== 'service-header') return null
                return undefined
            },
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'service_status',
            title: t('service_status'),
            width: 130,
            render: (_, row) => {
                if (row._rowType !== 'service-header') return null
                return undefined
            },
            formatter: createOptionsFormatter({
                options: SERVICE_STATUS_OPTIONS(t),
            }),
        },
        {
            key: 'service_active_cycling',
            title: t('service_active_cycling'),
            width: 180,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType !== 'service-header') return null
                return undefined
            },
            formatter: createTimeRangeFormatter({
                startKey: 'current_startdate',
                endKey: 'current_enddate',
                format: 'DD/MM/YYYY',
                smartYear: true,
            }),
        },
        {
            key: 'service_renew_cycling',
            title: t('service_renew_cycling'),
            width: 180,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType !== 'service-header') return null
                const validStatuses = [
                    ServiceStatus.Active,
                    ServiceStatus.WaitingCreateService,
                    ServiceStatus.WaitingConfirm,
                ]
                if (
                    !validStatuses.includes(
                        row?.service_status as ServiceStatus
                    )
                )
                    return null
                return undefined
            },
            formatter: createTimeRangeFormatter({
                startKey: 'continue_startdate',
                endKey: 'continue_enddate',
                format: 'DD/MM/YYYY',
                smartYear: true,
            }),
        },
        {
            key: 'service_stopped_date',
            title: t('service_stopped_date'),
            width: 110,
            cellClassName: 'text-right',
            render: (_, row) => {
                if (row._rowType !== 'service-header') return null
                return undefined
            },
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'service_contract',
            title: t('service_contract'),
            width: 300,
            render: (_, row) => {
                if (row._rowType !== 'service-header') return null
                if (!row.service_id) return null
                return <ServiceContract serviceID={row.service_id} />
            },
        }
    )

    return columns
}
